import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { FolderInput, LoaderCircle } from 'lucide-react'

import * as Mutation from '../../../api/gql/mutation'
import FolderPicker from './FolderPicker'

interface PublishDialogProps {
  submittalId: string
  projectId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  refetch: () => void
  /** `workflowPlan.targetFolderId` snapshotted onto the revision at submit
   *  time (see src/graphql/submittals/lifecycle.ts) — the workflow
   *  template row itself is never re-consulted here. Only pre-selects the
   *  FolderPicker; the user can still change it before confirming. Callers
   *  MUST source this from the submittal's newest revision (e.g.
   *  `currentRevisionFull` in index.tsx), never from a revision picker's
   *  current selection — `publishSubmittalToDocControl` always publishes
   *  the newest revision server-side regardless of what's on screen. */
  defaultFolderId?: string | null
}

/**
 * Standalone "publish later" path for an already-approved submittal — the
 * header action hides itself once currentRevision.publishedAt is set (see
 * index.tsx), so this dialog only ever runs once per revision. Shares
 * FolderPicker with ConsolidateDialog's inline "publish now" section rather
 * than owning a second copy of the folder-tree fetch + render.
 */
const PublishDialog: React.FC<PublishDialogProps> = ({
  submittalId,
  projectId,
  open,
  onOpenChange,
  refetch,
  defaultFolderId,
}) => {
  const { t } = useTranslation()
  const [folderId, setFolderId] = useState<string | null>(defaultFolderId ?? null)
  const [publishToDocControl, { loading: submitting }] = useMutation(Mutation.PUBLISH_SUBMITTAL_TO_DOC_CONTROL)

  const handleClose = () => {
    if (submitting) return
    onOpenChange(false)
  }

  const handleConfirm = async () => {
    if (!folderId) return
    try {
      await publishToDocControl({ variables: { id: submittalId, folderId } })
      toastr.success('', t('Submittals_publish_success'))
      refetch()
      onOpenChange(false)
    } catch (error: any) {
      toastr.error(t('Common_error'), error.message)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) handleClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FolderInput className="h-5 w-5 text-primary" />
            {t('Submittals_publish_title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>{t('Submittals_publish_folder_label')}</Label>
          <FolderPicker projectId={projectId} value={folderId} onChange={setFolderId} />
        </div>

        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={submitting || !folderId}>
            {submitting && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Submittals_publish_confirm_btn')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PublishDialog
